const loadedScriptPromises = new Map<string, Promise<void>>()
const loadedStylePromises = new Map<string, Promise<void>>()

const RECURLY_SCRIPT_SRC = 'https://js.recurly.com/v4/recurly.js'
const RECURLY_STYLE_SRC = 'https://js.recurly.com/v4/recurly.css'
const UNBOUNCE_SCRIPT_SRC = 'https://2b5b197e62ef4618bc48ba5c04509523.js.ubembed.com'
const NEW_RELIC_AGENT_SRC = 'https://js-agent.newrelic.com/nr-spa-1216.min.js'
const NEW_RELIC_LICENSE_KEY = 'NRJS-64c927c46b4f1656bfb'
const NEW_RELIC_ACCOUNT_ID = '3555206'
const HUBSPOT_TRACKING_SCRIPT_ID = 'hs-script-loader'
const PARTNERSTACK_SCRIPT_SRC = 'https://js.partnerstack.com/v1/'
const PARTNERSTACK_PUBLIC_KEY = 'pk_I3292l5vELVEzruM6sQt6EUVGxazbGnm'

let recurlyReadyPromise: Promise<void> | null = null
let recurlyConfigured = false
let partnerStackInitialized = false

const isBrowserEnvironment = () => typeof window !== 'undefined' && typeof document !== 'undefined'

const waitForCondition = (
  condition: () => boolean,
  {
    timeoutMs = 10000,
    intervalMs = 50,
    errorMessage = 'Timed out waiting for third-party script to initialize.',
  } = {}
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (condition()) {
      resolve()
      return
    }

    const startedAt = Date.now()

    const checkCondition = () => {
      if (condition()) {
        resolve()
        return
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error(errorMessage))
        return
      }

      window.setTimeout(checkCondition, intervalMs)
    }

    checkCondition()
  })

const isEnabledByEnv = (envValue?: string, defaultValue = true) => {
  if (typeof envValue === 'undefined') return defaultValue
  return ['1', 'true', 'yes', 'on'].includes(envValue.toLowerCase())
}

const isProductionLikeEnvironment = () => {
  const currentEnvironment = (process.env.VUE_APP_ENVIRONMENT || '').toLowerCase()
  return currentEnvironment === 'production' || currentEnvironment === 'staging'
}

export const shouldInitializeNewRelic = () =>
  isEnabledByEnv(process.env.VUE_APP_ENABLE_NEW_RELIC, isProductionLikeEnvironment())

export const shouldInitializeHubspotTracking = () =>
  isEnabledByEnv(process.env.VUE_APP_ENABLE_HUBSPOT, isProductionLikeEnvironment())

export const loadScriptOnce = (
  src: string,
  attributes: Partial<HTMLScriptElement> = {}
): Promise<void> => {
  if (!isBrowserEnvironment()) {
    return Promise.resolve()
  }

  if (loadedScriptPromises.has(src)) {
    return loadedScriptPromises.get(src) as Promise<void>
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src="${src}"]`
    ) as HTMLScriptElement | null
    const script = existingScript || document.createElement('script')

    const onLoad = () => {
      script.dataset.loaded = 'true'
      resolve()
    }

    const onError = () => {
      loadedScriptPromises.delete(src)
      reject(new Error(`Failed to load script: ${src}`))
    }

    const readyState = (script as HTMLScriptElement & { readyState?: string }).readyState
    const hasLoadedResourceTimingEntry = (() => {
      if (!window.performance?.getEntriesByName) return false
      return window.performance.getEntriesByName(src).length > 0
    })()
    const isAlreadyLoaded =
      script.dataset.loaded === 'true' ||
      readyState === 'loaded' ||
      readyState === 'complete' ||
      Boolean(existingScript?.getAttribute('data-loaded') === 'true') ||
      hasLoadedResourceTimingEntry

    if (isAlreadyLoaded) {
      resolve()
      return
    }

    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })

    if (!existingScript) {
      script.src = src
      Object.assign(script, attributes)
      script.async = attributes.async ?? true
      document.body.appendChild(script)
      return
    }

    // Existing script may have already fired "load" before listeners were attached.
    // If that happened, avoid hanging forever by resolving after a short safety window.
    window.setTimeout(() => {
      const refreshedReadyState = (script as HTMLScriptElement & { readyState?: string }).readyState
      const hasLoadedNow =
        script.dataset.loaded === 'true' ||
        refreshedReadyState === 'loaded' ||
        refreshedReadyState === 'complete' ||
        (window.performance?.getEntriesByName?.(src)?.length ?? 0) > 0

      if (hasLoadedNow) {
        script.dataset.loaded = 'true'
        resolve()
      }
    }, 1200)
  })

  loadedScriptPromises.set(src, promise)
  return promise
}

export const loadStyleOnce = (href: string): Promise<void> => {
  if (!isBrowserEnvironment()) {
    return Promise.resolve()
  }

  if (loadedStylePromises.has(href)) {
    return loadedStylePromises.get(href) as Promise<void>
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existingStyle = document.querySelector(`link[href="${href}"]`) as HTMLLinkElement | null
    const style = existingStyle || document.createElement('link')

    const onLoad = () => {
      style.dataset.loaded = 'true'
      resolve()
    }

    const onError = () => {
      loadedStylePromises.delete(href)
      reject(new Error(`Failed to load stylesheet: ${href}`))
    }

    const isAlreadyLoaded =
      style.dataset.loaded === 'true' ||
      Boolean(existingStyle?.getAttribute('data-loaded') === 'true')
    if (isAlreadyLoaded) {
      resolve()
      return
    }

    style.addEventListener('load', onLoad, { once: true })
    style.addEventListener('error', onError, { once: true })

    if (!existingStyle) {
      style.rel = 'stylesheet'
      style.href = href
      document.head.appendChild(style)
    }
  })

  loadedStylePromises.set(href, promise)
  return promise
}

const runWhenIdle = (callback: () => void) => {
  if (!isBrowserEnvironment()) return
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => callback())
    return
  }
  window.setTimeout(() => callback(), 0)
}

const runAfterWindowLoad = (callback: () => void) => {
  if (!isBrowserEnvironment()) return
  if (document.readyState === 'complete') {
    callback()
    return
  }
  window.addEventListener('load', callback, { once: true })
}

const initializeUnbounce = () => loadScriptOnce(UNBOUNCE_SCRIPT_SRC, { async: true, defer: true })

const ignorePromiseResult = (promise: Promise<unknown>) => {
  promise.catch(() => {})
}

export const initializeNewRelic = async () => {
  const appId = process.env.VUE_APP_NEW_RELIC_FECODE
  if (!appId) return

  const windowWithNr = window as any
  windowWithNr.NREUM = windowWithNr.NREUM || {}
  windowWithNr.NREUM.init = {
    distributed_tracing: { enabled: true },
    privacy: { cookies_enabled: true },
    ajax: { deny_list: ['bam.nr-data.net'] },
  }
  windowWithNr.NREUM.loader_config = {
    accountID: NEW_RELIC_ACCOUNT_ID,
    trustKey: NEW_RELIC_ACCOUNT_ID,
    agentID: appId,
    licenseKey: NEW_RELIC_LICENSE_KEY,
    applicationID: appId,
  }
  windowWithNr.NREUM.info = {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: NEW_RELIC_LICENSE_KEY,
    applicationID: appId,
    sa: 1,
  }

  await loadScriptOnce(NEW_RELIC_AGENT_SRC, { async: true, defer: true })
}

export const initializeHubspotTracking = async () => {
  const hubspotScriptSrc = process.env.VUE_APP_HUBSPOT_SCRIPT
  if (!hubspotScriptSrc) return

  await loadScriptOnce(hubspotScriptSrc, {
    id: HUBSPOT_TRACKING_SCRIPT_ID,
    async: true,
    defer: true,
  })
}

export const ensurePartnerStackReady = async () => {
  if (!isBrowserEnvironment()) return

  if (partnerStackInitialized) return

  await loadScriptOnce(PARTNERSTACK_SCRIPT_SRC, { async: true, defer: true })
  await waitForCondition(
    () =>
      Boolean(
        (window as any).growsumo && typeof (window as any).growsumo._initialize === 'function'
      ),
    {
      errorMessage: 'Timed out waiting for PartnerStack to initialize.',
    }
  )

  if (partnerStackInitialized) return
  ;(window as any).growsumo._initialize(PARTNERSTACK_PUBLIC_KEY)
  if (typeof (window as any).growsumoInit === 'function') {
    ;(window as any).growsumoInit()
  }
  partnerStackInitialized = true
}

export const initializeDeferredThirdPartyIntegrations = () => {
  if (!isBrowserEnvironment()) return

  runAfterWindowLoad(() => {
    runWhenIdle(() => {
      if (shouldInitializeNewRelic()) {
        ignorePromiseResult(initializeNewRelic())
      }
      if (isEnabledByEnv(process.env.VUE_APP_ENABLE_UNBOUNCE, isProductionLikeEnvironment())) {
        ignorePromiseResult(initializeUnbounce())
      }
      if (shouldInitializeHubspotTracking()) {
        ignorePromiseResult(initializeHubspotTracking())
      }
    })
  })
}

export const ensureRecurlyReady = async (recurlyPublicKey?: string) => {
  if (!isBrowserEnvironment()) return

  if (!recurlyReadyPromise) {
    const existingRecurlyStyle = document.querySelector(`link[href="${RECURLY_STYLE_SRC}"]`)
    const existingRecurlyScript = document.querySelector(`script[src="${RECURLY_SCRIPT_SRC}"]`)

    recurlyReadyPromise = Promise.all([
      existingRecurlyStyle ? Promise.resolve() : loadStyleOnce(RECURLY_STYLE_SRC),
      existingRecurlyScript
        ? Promise.resolve()
        : loadScriptOnce(RECURLY_SCRIPT_SRC, { async: true, defer: true }),
    ])
      .then(() =>
        waitForCondition(() => Boolean((window as any).recurly?.configure), {
          errorMessage: 'Timed out waiting for Recurly to initialize.',
        })
      )
      .then(() => undefined)
      .catch(error => {
        recurlyReadyPromise = null
        recurlyConfigured = false
        throw error
      })
  }

  await recurlyReadyPromise
  if (!recurlyConfigured && recurlyPublicKey && (window as any).recurly?.configure) {
    ;(window as any).recurly.configure(recurlyPublicKey)
    recurlyConfigured = true
  }
}
